import { injectable, inject } from 'inversify';
import { GTD_DOMAIN_SYMBOLS } from '../../../../infrastructure/di/GTDDomainSymbols';
import { NextActionAggregateRepository } from '../../infrastructure/repositories/NextActionAggregateRepository';
import { getMongoDb, Collection } from "@swoft/persistence";

/**
 * Query criteria for filtering actions
 */
export interface ActionQueryCriteria {
  readonly context?: string;
  readonly energyLevel?: string;
  readonly status?: 'available' | 'assigned' | 'completed' | 'all';
  readonly assignedTo?: string;
  readonly projectId?: string;
  readonly searchText?: string;
  readonly offset?: number;
  readonly limit?: number;
}

/**
 * Action view model for read operations
 */
export interface NextActionView {
  readonly id: string;
  readonly description: string;
  readonly context: string;
  readonly energyLevel: string;
  readonly estimatedMinutes: number;
  readonly status: 'available' | 'assigned' | 'completed';
  readonly createdBy: string;
  readonly createdAt: Date;
  readonly projectId?: string;
  readonly assignedTo?: string;
  readonly assignedAt?: Date;
  readonly completedAt?: Date;
}

/**
 * Query result with pagination
 */
export interface ActionQueryResult {
  readonly actions: NextActionView[];
  readonly total: number;
  readonly offset: number;
  readonly limit: number;
  readonly hasMore: boolean;
}

/**
 * NextActionReadService - Query Side (CQRS)
 * 
 * Handles all read operations for GTD next actions.
 * Optimized for the GTD "Engage" phase - choosing what to do based on context.
 */
@injectable()
export class NextActionReadService {
  private collection: Collection;

  constructor(
    @inject(GTD_DOMAIN_SYMBOLS.NextActionAggregateRepository)
    _repository: NextActionAggregateRepository
  ) {
    const db = getMongoDb();
    this.collection = db.collection('gtd_next_actions');
  }

  /**
   * Get actions matching current context and energy
   * Core GTD principle: Match actions to available resources
   */
  async getAvailableActions(criteria: ActionQueryCriteria): Promise<ActionQueryResult> {
    const {
      context,
      energyLevel,
      status = 'available',
      assignedTo,
      projectId,
      searchText,
      offset = 0,
      limit = 20
    } = criteria;

    // Build query
    const query: any = {};
    
    if (status !== 'all') {
      query.status = status;
    }
    
    if (context) {
      query.context = context;
    }
    
    if (energyLevel) {
      query.energyLevel = energyLevel;
    }
    
    if (assignedTo) {
      query.assignedTo = assignedTo;
    }
    
    if (projectId) {
      query.projectId = projectId;
    }
    
    if (searchText) {
      query.description = { $regex: searchText, $options: 'i' };
    }

    // Execute query with pagination
    const [actions, total] = await Promise.all([
      this.collection
        .find(query)
        .sort({ createdAt: -1 })
        .skip(offset)
        .limit(limit)
        .toArray(),
      this.collection.countDocuments(query)
    ]);

    // Map to view models
    const actionViews: NextActionView[] = actions.map(doc => ({
      id: doc._id.toString(),
      description: doc.description,
      context: doc.context,
      energyLevel: doc.energyLevel,
      estimatedMinutes: doc.estimatedMinutes,
      status: doc.status,
      createdBy: doc.createdBy,
      createdAt: doc.createdAt,
      projectId: doc.projectId,
      assignedTo: doc.assignedTo,
      assignedAt: doc.assignedAt,
      completedAt: doc.completedAt
    }));

    return {
      actions: actionViews,
      total,
      offset,
      limit,
      hasMore: total > offset + limit
    };
  }

  /**
   * Get a single action by ID
   */
  async getActionById(actionId: string): Promise<NextActionView | null> {
    const doc = await this.collection.findOne({ _id: actionId as any });
    
    if (!doc) {
      return null;
    }

    return {
      id: doc._id.toString(),
      description: doc.description,
      context: doc.context,
      energyLevel: doc.energyLevel,
      estimatedMinutes: doc.estimatedMinutes,
      status: doc.status,
      createdBy: doc.createdBy,
      createdAt: doc.createdAt,
      projectId: doc.projectId,
      assignedTo: doc.assignedTo,
      assignedAt: doc.assignedAt,
      completedAt: doc.completedAt
    };
  }

  /**
   * Get actions for GTD weekly review
   */
  async getActionsForReview(personId: string): Promise<ActionQueryResult> {
    const query = {
      $or: [
        { createdBy: personId },
        { assignedTo: personId }
      ]
    };

    const actions = await this.collection
      .find(query)
      .sort({ status: 1, createdAt: -1 })
      .toArray();

    const actionViews: NextActionView[] = actions.map(doc => ({
      id: doc._id.toString(),
      description: doc.description,
      context: doc.context,
      energyLevel: doc.energyLevel,
      estimatedMinutes: doc.estimatedMinutes,
      status: doc.status,
      createdBy: doc.createdBy,
      createdAt: doc.createdAt,
      projectId: doc.projectId,
      assignedTo: doc.assignedTo,
      assignedAt: doc.assignedAt,
      completedAt: doc.completedAt
    }));

    return {
      actions: actionViews,
      total: actionViews.length,
      offset: 0,
      limit: actionViews.length,
      hasMore: false
    };
  }

  /**
   * Get action statistics
   */
  async getActionStatistics(): Promise<{
    total: number;
    available: number;
    assigned: number;
    completed: number;
    byContext: Record<string, number>;
    byEnergy: Record<string, number>;
  }> {
    const [
      total,
      available,
      assigned,
      completed,
      byContext,
      byEnergy
    ] = await Promise.all([
      this.collection.countDocuments({}),
      this.collection.countDocuments({ status: 'available' }),
      this.collection.countDocuments({ status: 'assigned' }),
      this.collection.countDocuments({ status: 'completed' }),
      this.getCountByField('context'),
      this.getCountByField('energyLevel')
    ]);

    return {
      total,
      available,
      assigned,
      completed,
      byContext,
      byEnergy
    };
  }

  /**
   * Get counts grouped by a field
   */
  private async getCountByField(field: string): Promise<Record<string, number>> {
    const results = await this.collection.aggregate([
      {
        $group: {
          _id: `$${field}`,
          count: { $sum: 1 }
        }
      }
    ]).toArray();

    const counts: Record<string, number> = {};
    for (const result of results) {
      if (result._id) {
        counts[result._id] = result.count;
      }
    }
    return counts;
  }
}